import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker, { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { useSettings } from '../../core/contexts/SettingsContext';
import { resolveCompanyField } from '../../shared/components/settings/companySettingsSaudi';
import {
  calculateGOSIDeclarationLine,
  calculateGOSIDeclarationSummary,
  getGOSIPaymentDeadline,
  getGosiDeclRates,
  mapSalariesConfigToGosiSettings,
} from './utils/payrollSaudiFormat';
import {
  Shield, TrendingUp, Users, Plus, X, Filter, ChevronDown, ChevronUp, CheckCircle,
  Search, RefreshCcw, Clock, FileText, Calendar, Info, User, Copy, Download,
  Upload, FileSpreadsheet, FileOutput,
  XCircle, LayoutGrid, Layers
} from 'lucide-react';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  SalairesListControlsStrip,
  useClearSearchOnTableView,
  renderSalairesBulkDeleteFilterAction,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import {
  generateGOSI_Monthly_XML,
  generateSANED_Monthly_XML,
  generateWPS_Report_XML,
  generateDeclarationPDF,
  downloadFile
} from '../../shared/utils/declarationsGenerators';
import '../../core/styles/global-dropdowns.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useSalairesConfig } from './useSalairesConfig';

registerLocale('ar', ar);
registerLocale('ar-SA', ar);

const datePickerLocale = getBrandDatePickerLocale();

/** Compare HR employee ids (number or string from SQLite API). */
const sameEmployeeId = (a, b) => String(a) === String(b);

const FE_TO_API_DECL_TYPE = {
  gosi_monthly: 'gosi',
  saned_monthly: 'saned',
  wps_report: 'wps',
};

const API_TO_FE_DECL_TYPE = {
  gosi: 'gosi_monthly',
  saned: 'saned_monthly',
  wps: 'wps_report',
};

const mapApiDeclarationRow = (row) => ({
  id: String(row.id),
  type: API_TO_FE_DECL_TYPE[row.declaration_type] || row.declaration_type,
  periodicity: 'monthly',
  year: row.year,
  month: row.month,
  createdAt: row.created_at || row.createdAt,
  status: row.status || 'pending',
  employeeCount: row.employee_count ?? (Array.isArray(row.employees) ? row.employees.length : 0),
  fileGenerated: !!(row.file_generated || row.fileGenerated),
  fileName: row.file_name || row.fileName,
  gosiEstablishmentNumber: row.gosi_registration_number || row.employer_number,
  unifiedNumber: row.employer_number,
  employees: Array.isArray(row.employees)
    ? row.employees.map((e) => ({
        id: e.id,
        name: e.name || `${e.first_name || e.prenom || ''} ${e.last_name || e.nom || ''}`.trim(),
      }))
    : [],
});

const DeclarationsSocialesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.declarationsSocialesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_declaration', { defaultValue: 'إقرار' });
  const showNotification = useCallback((msg) => alert(msg), []);

  const MONTH_KEYS = [
    'month_jan', 'month_feb', 'month_mar', 'month_apr', 'month_may', 'month_jun',
    'month_jul', 'month_aug', 'month_sep', 'month_oct', 'month_nov', 'month_dec',
  ];

  const formatMonth = useCallback((month) => p(MONTH_KEYS[month - 1]), [p]);

  const getDeclarationType = useCallback((type) => {
    switch (type) {
      case 'gosi_monthly':
        return p('type_gosi');
      case 'saned_monthly':
        return p('type_saned');
      case 'wps_report':
        return p('type_wps');
      default:
        return type.toUpperCase();
    }
  }, [p]);

  const getDeclarationPeriod = useCallback((declaration) => {
    if (declaration.month) {
      return `${formatMonth(declaration.month)} ${declaration.year}`;
    }
    return `${declaration.year}`;
  }, [formatMonth]);

  const formatMoney = useCallback((amount) => formatBrandMoney(amount || 0, true, settings), [settings]);

  const companyDeclarationDefaults = useMemo(() => {
    const company = settings?.company || {};
    return {
      gosiEstablishmentNumber: company.gosiEstablishmentNumber || '',
      unifiedNumber: resolveCompanyField(company, 'unifiedNationalNumber', ['nis', 'articleNumber']) || '',
      commercialRegistration: resolveCompanyField(company, 'commercialRegistration', ['rc']) || '',
      companyNameAr: company.nameArabic || company.name || '',
      companyNameEn: company.name || company.nameArabic || '',
    };
  }, [settings?.company]);

  const formatDeadline = useCallback((year, month) => {
    if (!year || !month) return '';
    return getGOSIPaymentDeadline(year, month).toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [dateLocale]);

  const [loading, setLoading] = useState(true);
  const [declarations, setDeclarations] = useState([]);
  const { employees } = useSalairesEmployeeOptions();
  const salariesConfig = useSalairesConfig();
  const gosiSettings = useMemo(
    () => mapSalariesConfigToGosiSettings(salariesConfig),
    [salariesConfig],
  );
  const gosiDeclRates = useMemo(() => getGosiDeclRates(gosiSettings), [gosiSettings]);
  const [selectedType, setSelectedType] = useState('gosi_monthly');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterPeriodicity, setFilterPeriodicity] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedDeclaration, setExpandedDeclaration] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_declarations');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const [formData, setFormData] = useState({
    type: '',
    year: '',
    periodicity: 'monthly',
    month: '',
    employeeIds: [],
    gosiEstablishmentNumber: '',
    unifiedNumber: '',
    commercialRegistration: '',
    companyNameAr: '',
    companyNameEn: '',
    notes: ''
  });

  const [formTab, setFormTab] = useState('setup');

  const declarationFormTabs = useMemo(() => ([
    { id: 'setup', label: p('section_type_period'), icon: Calendar, iconClassName: 'text-blue-400' },
    { id: 'employees', label: p('section_employee_selection', { selected: formData.employeeIds.length, total: employees.length }), icon: Users, iconClassName: 'text-green-400' },
  ]), [p, formData.employeeIds.length, employees.length]);

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const reloadDeclarations = useCallback(async () => {
    setLoading(true);
    try {
      const yearNum = filterYear ? parseInt(filterYear, 10) : null;
      const apiType = FE_TO_API_DECL_TYPE[selectedType];
      const query = { ...(apiType ? { declaration_type: apiType } : {}), ...(yearNum ? { year: yearNum } : {}) };
      const response = await localApiAdapter.get('/salaries/gosi', query);
      const raw = response?.success && Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response) ? response : (response?.data || []));
      let mapped = sortRecordsNewestFirst(raw.map(mapApiDeclarationRow));
      if (filterPeriodicity) {
        mapped = mapped.filter((d) => !d.periodicity || d.periodicity === filterPeriodicity);
      }
      setDeclarations(mapped);
    } catch (error) {
      console.error('Failed to fetch declarations:', error);
      setDeclarations([]);
      showNotification(p('error_fetch'));
    } finally {
      setLoading(false);
    }
  }, [selectedType, filterYear, filterPeriodicity, showNotification, p]);

  useEffect(() => {
    reloadDeclarations();
  }, [reloadDeclarations]);

  const openCreateModal = () => {
    setFormData({
      type: '',
      year: String(new Date().getFullYear()),
      periodicity: 'monthly',
      month: String(new Date().getMonth() + 1),
      employeeIds: [],
      notes: '',
      ...companyDeclarationDefaults,
    });
    setFormTab('setup');
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormTab('setup');
    setFormData({
      type: '',
      year: '',
      periodicity: 'monthly',
      month: '',
      employeeIds: [],
      notes: '',
      ...companyDeclarationDefaults,
    });
    setShowCreateModal(false);
  };

  const handleCreateDeclaration = async () => {
    const declType = formData.type || selectedType;
    if (!formData.year) {
      notifyError(showNotify, p('validation_year'));
      return;
    }
    if (!formData.month) {
      notifyError(showNotify, p('validation_month'));
      return;
    }
    if (formData.employeeIds.length === 0) {
      notifyError(showNotify, p('validation_employees'));
      return;
    }

    try {
      const yearNum = parseInt(formData.year, 10);
      const payload = {
        declaration_type: FE_TO_API_DECL_TYPE[declType] || 'gosi',
        year: yearNum,
        month: parseInt(formData.month, 10),
        employee_ids: formData.employeeIds,
        employer_number: formData.unifiedNumber || formData.commercialRegistration,
        gosi_registration_number: formData.gosiEstablishmentNumber,
      };
      const result = await localApiAdapter.post('/salaries/gosi', payload);
      if (result?.success === false) {
        notifyError(showNotify, result.error || result.message || p('error_create'));
        return;
      }
      notifyCreated(showNotify, entityLabel);
      resetForm();
      await reloadDeclarations();
    } catch (error) {
      console.error('Failed to create declaration:', error);
      notifyError(showNotify, p('error_create'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/salaries/gosi/${id}`)));
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabel);
      await reloadDeclarations();
    } catch (error) {
      console.error('Failed bulk delete:', error);
      notifyError(showNotify, p('error_bulk_delete'));
    }
  };

  const handleDeleteDeclaration = async (declarationId) => {
    if (!window.confirm(p('confirm_delete'))) {
      return;
    }
    
    try {
      await localApiAdapter.delete(`/salaries/gosi/${declarationId}`);
      notifyDeleted(showNotify, entityLabel);
      await reloadDeclarations();
    } catch (error) {
      console.error('Failed to delete declaration:', error);
      notifyError(showNotify, p('error_delete'));
    }
  };

  /** Generate declaration files (XML + PDF) for GOSI / SANED / WPS. */
  const handleGenerateDeclaration = async (declarationId) => {
    try {
      const declaration = declarations.find(d => d.id === declarationId);
      if (!declaration) {
        showNotification(p('not_found'));
        return;
      }

      const fullEmployees = declaration.employees.map(emp =>
        employees.find(e => sameEmployeeId(e.id, emp.id))
      ).filter(Boolean);

      if (fullEmployees.length === 0) {
        showNotification(p('no_employees_for_declaration'));
        return;
      }

      const declPayload = {
        ...declaration,
        gosiEstablishmentNumber: declaration.gosiEstablishmentNumber || formData.gosiEstablishmentNumber,
        unifiedNumber: declaration.unifiedNumber || formData.unifiedNumber,
        commercialRegistration: declaration.commercialRegistration || formData.commercialRegistration,
        companyNameAr: declaration.companyNameAr || formData.companyNameAr,
        companyNameEn: declaration.companyNameEn || formData.companyNameEn,
      };

      let xmlContent = '';
      let xmlFileName = '';
      let pdfFileName = '';
      const monthPad = declaration.month ? String(declaration.month).padStart(2, '0') : '00';

      switch (declaration.type) {
        case 'gosi_monthly':
          xmlContent = generateGOSI_Monthly_XML(declPayload, fullEmployees);
          xmlFileName = `gosi_${declaration.year}_${monthPad}.xml`;
          pdfFileName = `gosi_${declaration.year}_${monthPad}.pdf`;
          break;
        case 'saned_monthly':
          xmlContent = generateSANED_Monthly_XML(declPayload, fullEmployees);
          xmlFileName = `saned_${declaration.year}_${monthPad}.xml`;
          pdfFileName = `saned_${declaration.year}_${monthPad}.pdf`;
          break;
        case 'wps_report':
          xmlContent = generateWPS_Report_XML(declPayload, fullEmployees);
          xmlFileName = `wps_${declaration.year}_${monthPad}.xml`;
          pdfFileName = `wps_${declaration.year}_${monthPad}.pdf`;
          break;
        default:
          showNotification(p('unknown_type'));
          return;
      }

      const gosiSummary = declaration.type === 'gosi_monthly'
        ? calculateGOSIDeclarationSummary(fullEmployees, gosiSettings)
        : null;

      const pdfDoc = generateDeclarationPDF(declPayload, fullEmployees, declaration.type);
      const pdfBlob = pdfDoc.output('blob');

      const generatedFiles = {
        xml: { content: xmlContent, filename: xmlFileName },
        pdf: { blob: pdfBlob, filename: pdfFileName }
      };

      setDeclarations(prev => prev.map(d =>
        d.id === declarationId
          ? {
              ...d,
              status: 'generated',
              fileGenerated: true,
              fileName: xmlFileName,
              pdfFileName: pdfFileName,
              generatedFiles,
              gosiSummary,
            }
          : d
      ));

      showNotification(p('generated_success'));
    } catch (error) {
      console.error('Failed to generate declaration:', error);
      showNotification(p('error_generate', { message: error.message }));
    }
  };

  /** Download generated declaration (XML and/or PDF). */
  const handleDownloadDeclaration = async (declarationId, fileType = 'both') => {
    try {
      const declaration = declarations.find(d => d.id === declarationId);
      
      if (!declaration) {
        showNotification(p('not_found'));
        return;
      }

      if (!declaration.fileGenerated || !declaration.generatedFiles) {
        showNotification(p('files_not_ready'));
        return;
      }

      const { xml, pdf } = declaration.generatedFiles;

      // Télécharger XML
      if (fileType === 'xml' || fileType === 'both') {
        downloadFile(xml.content, xml.filename, 'application/xml');
      }

      // Télécharger PDF
      if (fileType === 'pdf' || fileType === 'both') {
        const url = URL.createObjectURL(pdf.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdf.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      const message = fileType === 'both'
        ? p('download_both', { xml: xml.filename, pdf: pdf.filename })
        : fileType === 'xml'
          ? p('download_xml', { filename: xml.filename })
          : p('download_pdf', { filename: pdf.filename });

      showNotification(message);
    } catch (error) {
      console.error('Failed to download declaration:', error);
      showNotification(p('download_error', { message: error.message }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const declarationYearOptions = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => {
        const y = new Date().getFullYear() - 2 + i;
        return { value: String(y), label: String(y) };
      }),
    []
  );

  const periodicitySelectOptions = useMemo(
    () => [{ value: 'monthly', label: p('periodicity_monthly_option') }],
    [p]
  );

  const declarationMonthSelectOptions = useMemo(
    () => MONTH_KEYS.map((key, i) => ({ value: String(i + 1), label: p(key) })),
    [p]
  );

  const gosiTabSummary = useMemo(() => {
    if (selectedType !== 'gosi_monthly' || !employees.length) return null;
    return calculateGOSIDeclarationSummary(employees, gosiSettings);
  }, [selectedType, employees, gosiSettings]);

  const declarationTypeOptions = useMemo(
    () => [
      { value: 'gosi_monthly', label: p('type_gosi') },
      { value: 'saned_monthly', label: p('type_saned') },
      { value: 'wps_report', label: p('type_wps') },
    ],
    [p]
  );

  const stats = useMemo(() => ({
    total: declarations.length,
    generated: declarations.filter((d) => d.status === 'generated').length,
    pending: declarations.filter((d) => d.status === 'pending').length,
    employees: employees.length,
    activeType: selectedType === 'gosi_monthly' ? p('type_gosi_short') : selectedType === 'saned_monthly' ? p('type_saned') : p('type_wps'),
  }), [declarations, employees.length, selectedType, p]);

  const mainTabs = [
    { id: 'all_declarations', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_statut', name: t('common.by_status'), icon: Shield },
    { id: 'par_periodicite', name: p('tab_by_periodicity'), icon: Calendar },
    { id: 'par_annee', name: p('tab_by_year'), icon: FileText },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'generated', name: p('filter_generated'), icon: CheckCircle },
    { id: 'pending', name: p('status_pending'), icon: Clock },
  ];

  const declarationDistribution = useMemo(() => ({
    all_declarations: {
      title: p('overview'),
      data: [
        { filterKey: 'generated', name: p('dist_generated'), count: stats.generated, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'monthly', name: p('dist_monthly'), count: declarations.filter((d) => d.periodicity === 'monthly').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { filterKey: 'generated', name: p('dist_generated'), count: stats.generated, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_periodicite: {
      title: p('distribution_by_periodicity'),
      data: [
        { filterKey: 'monthly', name: p('dist_monthly'), count: declarations.filter((d) => d.periodicity === 'monthly').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_annee: {
      title: p('distribution_by_year'),
      data: Array.from(new Set(declarations.map((d) => d.year))).sort((a, b) => b - a).slice(0, 4).map((year) => ({
        filterKey: String(year),
        name: String(year),
        count: declarations.filter((d) => d.year === year).length,
        color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      })),
    },
  }), [declarations, stats, p]);

  const distributionInfo = declarationDistribution[mainViewMode] || declarationDistribution.all_declarations;

  const statusOptions = [
    { value: 'generated', label: p('status_generated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  ];

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('type', (row) => getDeclarationType(row.type)),
      textColumnRule('period', (row) => getDeclarationPeriod(row)),
      textColumnRule('year', (row) => String(row.year ?? '')),
      textColumnRule('employeeCount', (row) => String(row.employeeCount ?? '')),
      textColumnRule('createdAt', (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString(dateLocale) : '')),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [getDeclarationType, getDeclarationPeriod, dateLocale],
  );

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const handleStatusChange = (recordId, newStatus) => {
    setDeclarations((prev) => prev.map((d) => (d.id === recordId ? { ...d, status: newStatus } : d)));
  };

  const filteredDeclarations = useMemo(() => {
    let result = declarations.filter((declaration) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        getDeclarationType(declaration.type).toLowerCase().includes(searchLower) ||
        declaration.year.toString().includes(searchLower) ||
        (declaration.month && formatMonth(declaration.month).toLowerCase().includes(searchLower)) ||
        false;

      if (mainViewMode === 'all_declarations') {
        if (quickFilter === 'generated' && declaration.status !== 'generated') return false;
        if (quickFilter === 'pending' && declaration.status !== 'pending') return false;
      }

      if (activeDistributionFilter) {
        if (mainViewMode === 'all_declarations' || mainViewMode === 'par_statut') {
          if (activeDistributionFilter === 'generated' && declaration.status !== 'generated') return false;
          if (activeDistributionFilter === 'pending' && declaration.status !== 'pending') return false;
          if (activeDistributionFilter === 'monthly' && declaration.periodicity !== 'monthly') return false;
        }
        if (mainViewMode === 'par_periodicite') {
          if (activeDistributionFilter === 'monthly' && declaration.periodicity !== 'monthly') return false;
        }
        if (mainViewMode === 'par_annee' && String(declaration.year) !== activeDistributionFilter) return false;
      }

      return matchesSearch;
    });

    return applyColumnFilters(result, columnFilters, columnFilterRules);
  }, [declarations, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules, getDeclarationType, formatMonth]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredDeclarations);

  const modalDeclType = formData.type || selectedType;

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'type', label: p('col_type'), required: true },
    { key: 'period', label: p('col_period') },
    { key: 'year', label: p('col_year') },
    { key: 'employeeCount', label: p('col_employees') },
    { key: 'periodicity', label: p('col_periodicity') },
    { key: 'createdAt', label: p('col_created_at') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button type="button" onClick={reloadDeclarations} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button type="button" onClick={openCreateModal} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30">
            <Plus className="h-5 w-5" /><span>{p('new_declaration')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('kpi_total'), value: stats.total, icon: FileText, color: 'from-blue-500 to-blue-600' },
          { title: p('kpi_generated'), value: stats.generated, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('kpi_pending'), value: stats.pending, icon: Clock, color: 'from-orange-500 to-orange-600' },
          { title: p('kpi_employees'), value: stats.employees, icon: Users, color: 'from-purple-500 to-purple-600' },
          { title: p('kpi_active_type'), value: stats.activeType, icon: Shield, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_declarations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }} />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1">
          <button type="button" onClick={() => setSelectedType('gosi_monthly')} className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${selectedType === 'gosi_monthly' ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
            <Shield className="h-5 w-5" /><span>{p('type_gosi')}</span>
          </button>
          <button type="button" onClick={() => setSelectedType('saned_monthly')} className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${selectedType === 'saned_monthly' ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
            <FileText className="h-5 w-5" /><span>{p('type_saned')}</span>
          </button>
          <button type="button" onClick={() => setSelectedType('wps_report')} className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${selectedType === 'wps_report' ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
            <TrendingUp className="h-5 w-5" /><span>{p('type_wps')}</span>
          </button>
        </div>
      </div>

      {selectedType === 'gosi_monthly' && gosiTabSummary && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">{p('gosi_report_title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_registered')}</p>
              <p className="text-white font-bold text-lg">{gosiTabSummary.totalRegistered}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_saudi_count')}</p>
              <p className="text-white font-bold text-lg">{gosiTabSummary.saudiCount}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_expat_count')}</p>
              <p className="text-white font-bold text-lg">{gosiTabSummary.expatCount}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_total_basic')}</p>
              <p className="text-white font-bold">{formatMoney(gosiTabSummary.totalBasicSalaries)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_employee_contrib')}</p>
              <p className="text-white font-bold">{formatMoney(gosiTabSummary.totalEmployeeContrib)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_employer_contrib')}</p>
              <p className="text-white font-bold">{formatMoney(gosiTabSummary.totalEmployerContrib)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs">{p('kpi_deadline')}</p>
              <p className="text-white font-medium text-xs">{p('deadline_rule')}</p>
            </div>
          </div>
          <p className="text-white/45 text-xs mt-3">{p('gosi_cap_note', { cap: formatMoney(gosiDeclRates.gosiMaxBasic), employeeRate: gosiDeclRates.employeeRate, employerRate: gosiDeclRates.employerRate })}</p>
        </div>
      )}

      <div className="glass-panel glass-table-shell overflow-visible">
        <SalairesListControlsStrip
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          filterActions={renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
        />

        {loading ? (
          <div className="text-center py-12">
            <RefreshCcw className="animate-spin h-10 w-10 text-white/50 mx-auto mb-4" />
            <p className="text-white/50">{p('loading')}</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('type') ? <TableColumnHeaderFilter {...colHeaderFilterProps('type', p('col_type'))} /> : null}
                  {showCol('period') ? <TableColumnHeaderFilter {...colHeaderFilterProps('period', p('col_period'), { align: 'center' })} /> : null}
                  {showCol('year') ? <TableColumnHeaderFilter {...colHeaderFilterProps('year', p('col_year'), { align: 'center' })} /> : null}
                  {showCol('employeeCount') ? <TableColumnHeaderFilter {...colHeaderFilterProps('employeeCount', p('col_employees'), { align: 'center' })} /> : null}
                  {showCol('periodicity') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_periodicity')}</th> : null}
                  {showCol('createdAt') ? <TableColumnHeaderFilter {...colHeaderFilterProps('createdAt', p('col_created_at'), { align: 'center' })} /> : null}
                  {showCol('status') ? (
                  <TableColumnHeaderFilter
                    {...colHeaderFilterProps('status', p('col_status'), {
                      align: 'center',
                      kind: 'select',
                      options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                    })}
                  />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredDeclarations.length > 0 ? filteredDeclarations.map((declaration, index) => (
                  <React.Fragment key={declaration.id}>
                    <tr className={`hover:bg-white/5 transition-colors cursor-pointer ${selectedRecords.includes(declaration.id) ? 'bg-white/10' : ''}`} onClick={() => setExpandedDeclaration(expandedDeclaration === declaration.id ? null : declaration.id)}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(declaration.id)}
                        onChange={() => handleSelectRecord(declaration.id)}
                      />
                      {showCol('type') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{getDeclarationType(declaration.type)}</span></td>
                      ) : null}
                      {showCol('period') ? (
                      <td className="px-6 py-4 text-center"><span className="text-white/80 text-sm">{getDeclarationPeriod(declaration)}</span></td>
                      ) : null}
                      {showCol('year') ? (
                      <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{declaration.year}</span></td>
                      ) : null}
                      {showCol('employeeCount') ? (
                      <td className="px-6 py-4 text-center"><div className="flex items-center justify-center gap-2"><Users size={16} className="text-purple-400" /><span className="text-white font-semibold">{declaration.employeeCount}</span></div></td>
                      ) : null}
                      {showCol('periodicity') ? (
                      <td className="px-6 py-4 text-center">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">{p('periodicity_monthly')}</span>
                      </td>
                      ) : null}
                      {showCol('createdAt') ? (
                      <td className="px-6 py-4 text-center"><span className="text-white/60 text-xs">{formatDate(declaration.createdAt)}</span></td>
                      ) : null}
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={declaration.status}
                            recordId={declaration.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                            title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <QawaemTableStickyActionsTd onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {declaration.status !== 'generated' && (
                            <button type="button" onClick={() => handleGenerateDeclaration(declaration.id)} className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all" title={p('action_generate_title')}><Copy size={16} /></button>
                          )}
                          {declaration.fileGenerated && (
                            <div className="relative group">
                              <button type="button" onClick={() => handleDownloadDeclaration(declaration.id, 'both')} className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded-lg transition-all" title={p('action_download_title')}><Download size={16} /></button>
                              <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-white/20 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity z-50 min-w-[120px]">
                                <button type="button" onClick={() => handleDownloadDeclaration(declaration.id, 'xml')} className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 first:rounded-t-lg transition-colors flex items-center gap-2"><FileText size={16} className="text-blue-400" />{p('format_xml')}</button>
                                <button type="button" onClick={() => handleDownloadDeclaration(declaration.id, 'pdf')} className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-2"><FileText size={16} className="text-red-400" />{p('format_pdf')}</button>
                                <button type="button" onClick={() => handleDownloadDeclaration(declaration.id, 'both')} className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 last:rounded-b-lg transition-colors flex items-center gap-2 border-t border-white/10"><Download size={16} className="text-purple-400" />{p('download_all')}</button>
                              </div>
                            </div>
                          )}
                          <QawaemErpRowActions
                            compact
                            size={14}
                            onView={() => setExpandedDeclaration(expandedDeclaration === declaration.id ? null : declaration.id)}
                            onDelete={() => handleDeleteDeclaration(declaration.id)}
                            viewTitle={p('action_view_details', { defaultValue: t('common.view_details', { defaultValue: 'عرض التفاصيل' }) })}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </QawaemTableStickyActionsTd>
                    </tr>
                    <AnimatePresence>
                      {expandedDeclaration === declaration.id && declaration.employees && (
                        <tr key={`details-${declaration.id}`}>
                          <td colSpan={visibleTableColCount} className="p-0 border-t border-white/10 bg-white/5">
                            <div className="p-6">
                              <h4 className="text-white font-semibold mb-4 flex items-center gap-2"><Users size={18} className="text-blue-400" />{p('employees_included', { count: declaration.employeeCount })}</h4>
                              {declaration.type === 'gosi_monthly' && declaration.gosiSummary && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                                  <div className="text-white/70">{p('kpi_saudi_count')}: <span className="text-white font-semibold">{declaration.gosiSummary.saudiCount}</span></div>
                                  <div className="text-white/70">{p('kpi_expat_count')}: <span className="text-white font-semibold">{declaration.gosiSummary.expatCount}</span></div>
                                  <div className="text-white/70">{p('kpi_employee_contrib')}: <span className="text-white font-semibold">{formatMoney(declaration.gosiSummary.totalEmployeeContrib)}</span></div>
                                  <div className="text-white/70">{p('kpi_employer_contrib')}: <span className="text-white font-semibold">{formatMoney(declaration.gosiSummary.totalEmployerContrib)}</span></div>
                                </div>
                              )}
                              {declaration.month && (
                                <p className="text-white/50 text-sm mb-4">{p('payment_deadline')}: {formatDeadline(declaration.year, declaration.month)}</p>
                              )}
                              <div className="glass-table-scroll overflow-x-auto">
                                <table className="w-full">
                                  <thead className="glass-table-head"><tr>
                                    <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">{p('col_full_name')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_national_id')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_gosi_number')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_basic_salary')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_allowances')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_employee_contrib')}</th>
                                    <th className="px-4 py-3 text-center text-white/80 text-sm font-semibold">{p('col_employer_contrib')}</th>
                                  </tr></thead>
                                  <tbody className="divide-y divide-white/10">
                                    {declaration.employees.map((employee) => {
                                      const employeeData = employees.find((e) => sameEmployeeId(e.id, employee.id));
                                      const line = employeeData ? calculateGOSIDeclarationLine(employeeData, gosiSettings) : null;
                                      return (
                                        <tr key={employee.id} className="hover:bg-white/5">
                                          <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center"><User size={16} className="text-blue-400" /></div><span className="text-white font-medium">{employee.name}</span></div></td>
                                          <td className="px-4 py-3 text-center"><span className="text-white/70 text-sm font-mono">{employeeData?.nationalId || employeeData?.iqamaNumber || employeeData?.cin || p('not_available')}</span></td>
                                          <td className="px-4 py-3 text-center"><span className="text-white/70 text-sm font-mono">{employeeData?.gosiNumber || employeeData?.socialSecurityNumber || p('not_available')}</span></td>
                                          <td className="px-4 py-3 text-center"><span className="text-white/80 text-sm">{line ? formatMoney(line.basic) : p('not_available')}</span></td>
                                          <td className="px-4 py-3 text-center"><span className="text-white/80 text-sm">{line ? formatMoney(line.allowances) : p('not_available')}</span></td>
                                          <td className="px-4 py-3 text-center"><span className="text-green-300 text-sm font-medium">{line ? formatMoney(line.employeeContrib) : '—'}</span></td>
                                          <td className="px-4 py-3 text-center"><span className="text-blue-300 text-sm font-medium">{line ? formatMoney(line.employerContrib) : '—'}</span></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                )) : (
                  <tr><td colSpan={visibleTableColCount} className="px-6 py-12 text-center"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/50 text-lg mb-2">{p('empty_title')}</p><p className="text-white/40">{p('empty_hint')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{filteredDeclarations.length > 1 ? p('footer_count_plural', { count: filteredDeclarations.length }) : p('footer_count', { count: filteredDeclarations.length })}</span>
                      <span>{p('footer_employees_total')} <span className="text-white font-semibold">{filteredDeclarations.reduce((sum, d) => sum + (d.employeeCount || 0), 0)}</span></span>
                      <span>{p('footer_generated')} <span className="text-white font-semibold">{filteredDeclarations.filter((d) => d.status === 'generated').length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {filteredDeclarations.length > 0 ? filteredDeclarations.map((declaration, index) => (
              <motion.div key={declaration.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2"><Shield size={20} className="text-blue-400" />{getDeclarationType(declaration.type)}</h3>
                      <QawaemClickableStatusBadge
                        status={declaration.status}
                        recordId={declaration.id}
                        options={statusOptions}
                        onChange={handleStatusChange}
                      />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_period')}</span> {getDeclarationPeriod(declaration)}</div>
                          <div><span className="font-medium">{p('label_year')}</span> {declaration.year}</div>
                          <div><span className="font-medium">{p('label_periodicity')}</span> {p('periodicity_monthly')}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_employees')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_headcount')}</span> <span className="text-white font-bold text-lg">{declaration.employeeCount}</span></div>
                          <div><span className="font-medium">{p('label_created_at')}</span> {formatDate(declaration.createdAt)}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_files')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_file_status')}</span> {declaration.fileGenerated ? p('files_generated') : p('files_not_generated')}</div>
                          {declaration.fileName && <div><span className="font-medium">{p('label_xml')}</span> {declaration.fileName}</div>}
                          {declaration.pdfFileName && <div><span className="font-medium">{p('label_pdf')}</span> {declaration.pdfFileName}</div>}
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_quick_actions')}</h5>
                        <div className="flex flex-wrap gap-2">
                          {declaration.status !== 'generated' && <button type="button" onClick={() => handleGenerateDeclaration(declaration.id)} className="px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg text-sm">{p('action_generate')}</button>}
                          {declaration.fileGenerated && <button type="button" onClick={() => handleDownloadDeclaration(declaration.id, 'both')} className="px-3 py-1.5 bg-purple-500/20 text-purple-300 rounded-lg text-sm">{p('action_download')}</button>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => setExpandedDeclaration(expandedDeclaration === declaration.id ? null : declaration.id)}
                    onDelete={() => handleDeleteDeclaration(declaration.id)}
                    viewTitle={p('action_view_details', { defaultValue: t('common.view_details', { defaultValue: 'عرض التفاصيل' }) })}
                    deleteTitle={t('common.delete')}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="text-center py-12"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>
      
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-200">
            {selectedType === 'gosi_monthly' && (
              <>
                <strong>{p('info_gosi_title')}</strong><br />
                • {p('info_gosi_line1')}<br />
                • {p('info_gosi_line2')}<br />
                • {p('info_gosi_line3')}
              </>
            )}
            {selectedType === 'saned_monthly' && (
              <>
                <strong>{p('info_saned_title')}</strong><br />
                • {p('info_saned_line1')}<br />
                • {p('info_saned_line2')}<br />
                • {p('info_saned_line3')}
              </>
            )}
            {selectedType === 'wps_report' && (
              <>
                <strong>{p('info_wps_title')}</strong><br />
                • {p('info_wps_line1')}<br />
                • {p('info_wps_line2')}<br />
                • {p('info_wps_line3')}
              </>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-shrink-0 items-start sm:items-center justify-between gap-3 p-6 border-b border-white/10">
              <h2 className="text-xl sm:text-2xl font-bold text-white min-w-0 flex-1 pr-2 leading-tight">
                <span className="block truncate">{p('modal_new_title', { type: getDeclarationType(modalDeclType) })}</span>
              </h2>
              <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
                <button type="submit" form="declaration-form" disabled={formData.employeeIds.length === 0} className="hidden sm:inline-flex px-5 sm:px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed">
                  <CheckCircle size={18} />{p('modal_save')}
                </button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors p-1" aria-label={p('close_aria')}><X size={24} /></button>
              </div>
            </div>

            <QawaemErpModalFormTabBar tabs={declarationFormTabs} activeTab={formTab} onTabChange={setFormTab} isLight={isDashboardLightMode} ariaLabel={p('modal_new_title', { type: getDeclarationType(modalDeclType) })} />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="declaration-form" onSubmit={(e) => { e.preventDefault(); handleCreateDeclaration(); }}>
                {formTab === 'setup' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="md:col-span-3 min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('declaration_type_required')} required /></label>
                      <CustomSelect value={formData.type} onChange={(v) => setFormData({ ...formData, type: v })} options={declarationTypeOptions} placeholder={p('declaration_type_placeholder')} autoSelect={false} searchable creatable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('year_required')} required /></label>
                      <CustomSelect value={formData.year} onChange={(v) => setFormData({ ...formData, year: v })} options={declarationYearOptions} placeholder={p('year_placeholder')} autoSelect={false} searchable creatable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('month_required')} required /></label>
                      <CustomSelect value={formData.month} onChange={(v) => setFormData({ ...formData, month: v })} options={declarationMonthSelectOptions} placeholder={p('month_placeholder')} autoSelect={false} searchable creatable />
                    </div>
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-white/50 text-sm">{p('periodicity_monthly_only')}</p>
                      <p className="text-white/40 text-xs mt-1">{p('deadline_rule')}</p>
                    </div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('gosi_establishment_label')}</label><input type="text" value={formData.gosiEstablishmentNumber} onChange={(e) => setFormData({ ...formData, gosiEstablishmentNumber: e.target.value })} placeholder={p('gosi_establishment_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('unified_number_label')}</label><input type="text" value={formData.unifiedNumber} onChange={(e) => setFormData({ ...formData, unifiedNumber: e.target.value })} placeholder={p('unified_number_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('commercial_registration_label')}</label><input type="text" value={formData.commercialRegistration} onChange={(e) => setFormData({ ...formData, commercialRegistration: e.target.value })} placeholder={p('commercial_registration_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('company_name_ar_label')}</label><input type="text" value={formData.companyNameAr} onChange={(e) => setFormData({ ...formData, companyNameAr: e.target.value })} placeholder={p('company_name_ar_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" dir="rtl" /></div>
                    <div className="md:col-span-3 min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('company_name_en_label')}</label><input type="text" value={formData.companyNameEn} onChange={(e) => setFormData({ ...formData, companyNameEn: e.target.value })} placeholder={p('company_name_en_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                  </div>
                )}

                {formTab === 'employees' && (
                  <div className="space-y-4">
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                      {employees.length === 0 ? (
                        <div className="text-center py-8">
                          <Users size={48} className="text-white/30 mx-auto mb-3" />
                          <p className="text-white/50">{p('no_employees')}</p>
                          <p className="text-white/40 text-sm mt-2">{p('no_employees_hint')}</p>
                        </div>
                      ) : employees.map((employee) => (
                        <label key={employee.id} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all group border border-white/10 bg-white/[0.03] hover:bg-white/10 hover:border-white/20">
                          <input type="checkbox" checked={formData.employeeIds.some((id) => sameEmployeeId(id, employee.id))} onChange={(e) => {
                            if (e.target.checked) setFormData({ ...formData, employeeIds: [...formData.employeeIds, employee.id] });
                            else setFormData({ ...formData, employeeIds: formData.employeeIds.filter((id) => !sameEmployeeId(id, employee.id)) });
                          }} className="w-5 h-5 rounded-lg border-2 border-white/20 text-blue-500 focus:ring-2 focus:ring-blue-500 bg-white/10 transition-all" />
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                              {employee.prenom?.[0] || employee.firstName?.[0] || '?'}{employee.nom?.[0] || employee.lastName?.[0] || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium group-hover:text-blue-400 transition-colors truncate">{employee.prenom || employee.firstName} {employee.nom || employee.lastName}</p>
                              <p className="text-white/50 text-sm truncate">{employee.fonction || employee.poste || employee.position || p('undefined_position')}</p>
                            </div>
                            <span className="text-white/40 text-xs font-mono shrink-0">{employee.mat || employee.matricule || p('not_available')}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-0"><p className="text-white/50 mb-1">{p('summary_type')}</p><p className="text-white font-medium">{formData.type ? getDeclarationType(formData.type) : p('dash')}</p></div>
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-0"><p className="text-white/50 mb-1">{p('summary_year')}</p><p className="text-white font-medium">{formData.year || p('dash')}</p></div>
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-0"><p className="text-white/50 mb-1">{p('summary_period')}</p><p className="text-white font-medium">{formData.month ? formatMonth(parseInt(formData.month, 10)) : p('dash')}</p></div>
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-0"><p className="text-white/50 mb-1">{p('summary_employees')}</p><p className="text-white font-medium">{formData.employeeIds.length}</p></div>
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('internal_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" placeholder={p('notes_placeholder')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" />
                    </div>
                  </div>
                )}
              </form>
            </div>

            <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 pt-4 border-t border-white/10 bg-slate-900/40">
              <p className="text-xs sm:text-sm text-white/45 max-w-xl order-2 sm:order-1">{p('modal_footer_hint')}</p>
              <button type="submit" form="declaration-form" disabled={formData.employeeIds.length === 0} className="order-1 sm:order-2 w-full sm:w-auto justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed">
                <CheckCircle size={18} />{p('modal_save_declaration')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default DeclarationsSocialesPage; 


